import React from 'react';

import {
    faCircleBolt,
    faCircleExclamation,
    faParkingCircle,
    faSidebarFlip,
    faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { faChevronDown as fasChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import { PATH } from 'src/features/navigation/utils/constants';
import { pageTitles } from 'src/features/navigation/utils/messages';
import BarrierActionModalForm from 'src/features/parkingActions/components/BarrierActionModalForm';
import { barrierActions, emergencyBarrierActions } from 'src/features/parkingActions/routes/AllActions';
import { actionMessages } from 'src/features/parkingActions/utils/messages';
import { useParkingStatus } from 'src/features/parkingManagement/hooks/useParkingStatus';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils/messages';
import { useGetDevicesStatus } from 'src/features/parkingStatus/api/useGetDevicesStatus';
import { MEMBERSHIP_ROLES, type UserMembership } from 'src/features/userManagement/types';
import { useMediaQuery } from 'src/hooks/use-media-query.ts';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { toast } from 'src/registry/hooks/UseToast';
import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import { Button } from 'src/registry/ui/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuItemWithIcon,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'src/registry/ui/DropdownMenu';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { useTitleStore } from 'src/stores/useTitleStore';
import { cn } from 'src/utils';
import { generalMessages } from 'src/utils/messages';
import { storage } from 'src/utils/storage';

import type { Parking } from 'src/features/parkingManagement/types/parking';

interface HeaderProps extends React.HTMLAttributes<HTMLDivElement> {
    userMemberships: UserMembership[];
}

const Header = ({ userMemberships }: HeaderProps) => {
    const { formatMessage } = useIntl();
    const { parkingId, parkingName, setParkingId, setParkingName, setParkingRole, setParkingCurrency } = useParking();
    const permissionQuickActions = useHasPermissions([PERMISSION.PARKING_QUICK_ACTIONS]);
    const permissionDevicesStatus = useHasPermissions([PERMISSION.PARKING_DEVICES_STATUS]);

    const [pageTitle, setPageTitle] = React.useState('');
    const [currentErrorsChecked, setCurrentErrorsChecked] = React.useState(storage.getCurrentErrorsChecked());
    const { openPanel } = useSidePanel();

    const isMobile = useMediaQuery('(max-width: 639px)');
    const storeTitle = useTitleStore((state) => state.title);

    const { pathname } = useLocation();
    const navigate = useNavigate();

    const { available, isConnectionError } = useParkingStatus();

    // We also need to fetch the devices status periodically and show them in the header as buttons with a Link to the devices
    // page
    const {
        data: devicesStatus,
        isLoading: isLoadingStatus,
        errorUpdateCount: errorUpdateCountStatus,
    } = useGetDevicesStatus({
        parkingId: parkingId,
        config: {
            // Every 1 minute should be sufficient
            refetchInterval: 60 * 1000,
            refetchIntervalInBackground: false,
            enabled: !!parkingId && permissionDevicesStatus && available,
            refetchOnWindowFocus: true,
        },
    });

    // Map the page paths to a page title
    const getPageTitle = (path: PATH) => {
        switch (path) {
            case PATH.STATUS:
                return formatMessage(pageTitles.parkingStatus);
            case PATH.VEHICLES:
                return formatMessage(pageTitles.vehicles);
            case PATH.BILLING:
                return formatMessage(pageTitles.billing);
            case PATH.STATISTICS:
                return formatMessage(pageTitles.statistics);
            case PATH.USER_MANAGEMENT:
                return formatMessage(pageTitles.users);
            case PATH.PARKING_MANAGEMENT:
                return formatMessage(pageTitles.parkings);
            case PATH.PROFILE:
                return formatMessage(pageTitles.profile);
            case PATH.ACTIONS:
                return formatMessage(pageTitles.actions);
            case PATH.PAY_STATIONS:
                return formatMessage(pageTitles.payStations);
            case PATH.VALIDATION:
                return formatMessage(pageTitles.validation);
            case PATH.CASH_COLLECTIONS:
                return formatMessage(pageTitles.cashCollections);
            case PATH.PARKING_ADMIN:
                return formatMessage(pageTitles.parkingsAdmin);
            default:
                return formatMessage(pageTitles.smartPark);
        }
    };

    const handleParkingChange = (parking: Parking, role: MEMBERSHIP_ROLES) => {
        setParkingId(parking.id);
        setParkingName(parking.name);
        setParkingRole(role);
        setParkingCurrency(parking.default_currency);
        void navigate('/');
        toast({
            title: formatMessage(parkingManagementMessages.currentParkingChangeSuccess),
            description: (
                <FormattedMessage
                    {...parkingManagementMessages.currentParkingChangeSuccessDetails}
                    values={{
                        parkingName: parking.name,
                        b: (chunks: React.ReactNode[]) => <b className="font-semibold">{chunks}</b>,
                    }}
                />
            ),
        });
    };

    const pathRoot = React.useMemo(() => {
        // We need to get the first part of the pathname
        return pathname.split('/')[1] as PATH;
    }, [pathname]);

    React.useEffect(() => {
        let pathName = '';
        if (pathRoot) {
            pathName = getPageTitle(pathRoot);
        }

        setPageTitle(pathName || formatMessage(pageTitles.home));
    }, [pathRoot]);

    React.useEffect(() => {
        if (!devicesStatus) return;

        const currentErrors = storage.getCurrentErrors();
        if (devicesStatus.errors > currentErrors) {
            storage.setCurrentErrorsChecked(false);
            setCurrentErrorsChecked(false);
        }
        if (devicesStatus.errors != currentErrors) {
            storage.setCurrentErrors(devicesStatus.errors);
        }
    }, [devicesStatus]);

    const isParkingManagementScreen = pathname.includes(`${PATH.PARKING_MANAGEMENT}/`) && !isMobile;

    const iconSize = isParkingManagementScreen ? 'smIcon' : 'mdIcon';

    const parkingManagementSidePanelBtn = () => {
        return pathname.includes(`${PATH.PARKING_MANAGEMENT}/`) && isMobile ? (
            <Button
                size="icon"
                variant="outline"
                onClick={() => {
                    openPanel('parking-management-side-panel');
                }}
            >
                <FontAwesomeIcon icon={faSidebarFlip} />
            </Button>
        ) : null;
    };

    return (
        <header className="-my-2 flex items-center justify-between gap-4 overflow-hidden bg-white py-2 sm:sticky sm:top-0 sm:z-30 sm:-mx-8 sm:px-8 dark:bg-gray-900">
            <h1 className="relative text-3xl font-semibold tracking-tight sm:text-nowrap">
                <div className="flex items-center gap-4">
                    {parkingManagementSidePanelBtn()}
                    {storeTitle || pageTitle}
                </div>
            </h1>
            {pathRoot !== PATH.PAY_STATIONS &&
                pathRoot !== PATH.CASH_COLLECTIONS &&
                pathRoot !== PATH.PARKING_ADMIN && (
                    <div className="fixed top-2.5 right-4 z-50 mr-0.5 flex min-w-0 items-center gap-4 sm:relative sm:inset-0 sm:m-0">
                        {available && !isConnectionError && (
                            <>
                                {permissionDevicesStatus && (
                                    <>
                                        {isLoadingStatus && !errorUpdateCountStatus ? (
                                            <>
                                                <Skeleton className="size-10 lg:w-36" />
                                                <Skeleton className="size-10 lg:w-36" />
                                            </>
                                        ) : (
                                            <>
                                                {devicesStatus && devicesStatus.warnings > 0 && (
                                                    <Button size="lgIcon" variant="warning" asChild>
                                                        <Link to="/status">
                                                            <FontAwesomeIcon icon={faCircleExclamation} />
                                                            <span
                                                                className={`hidden ${isParkingManagementScreen ? 'lg:hidden xl:block' : 'lg:block'}`}
                                                            >
                                                                {formatMessage(generalMessages.warnings, {
                                                                    count: devicesStatus.warnings,
                                                                })}
                                                            </span>
                                                        </Link>
                                                    </Button>
                                                )}

                                                {devicesStatus && devicesStatus.errors > 0 && (
                                                    <Button
                                                        className={cn(
                                                            !currentErrorsChecked && 'animate-pulse hover:animate-none',
                                                        )}
                                                        size="lgIcon"
                                                        variant="destructive"
                                                        onClick={() => {
                                                            setCurrentErrorsChecked(true);
                                                            storage.setCurrentErrorsChecked(true);
                                                        }}
                                                        asChild
                                                    >
                                                        <Link to="/status">
                                                            <FontAwesomeIcon icon={faTriangleExclamation} />
                                                            <span
                                                                className={`hidden ${isParkingManagementScreen ? 'lg:hidden xl:block' : 'lg:block'}`}
                                                            >
                                                                {formatMessage(generalMessages.crititalErrors, {
                                                                    count: devicesStatus.errors,
                                                                })}
                                                            </span>
                                                        </Link>
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                                {permissionQuickActions && (
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button size={iconSize} variant="main">
                                                <FontAwesomeIcon
                                                    icon={faCircleBolt}
                                                    className={`text-base ${isParkingManagementScreen ? 'md:block' : 'md:hidden'}`}
                                                />
                                                <span
                                                    className={`hidden ${isParkingManagementScreen ? 'md:hidden xl:block' : 'md:block'}`}
                                                >
                                                    {formatMessage(actionMessages.quickActions)}
                                                </span>
                                                <FontAwesomeIcon
                                                    icon={fasChevronDown}
                                                    className={`hidden text-xs ${isParkingManagementScreen ? 'md:hidden xl:block' : 'md:block'}`}
                                                />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent className="z-40" align="end">
                                            <DropdownMenuLabel>
                                                {formatMessage(actionMessages.quickActions)}
                                            </DropdownMenuLabel>
                                            <DropdownMenuSeparator />
                                            {barrierActions.map((action) => (
                                                <BarrierActionModalForm
                                                    triggerComponent={
                                                        <DropdownMenuItemWithIcon
                                                            onSelect={(e) => e.preventDefault()}
                                                            key={action.action}
                                                            icon={action.icon}
                                                        >
                                                            {formatMessage(action.title)}
                                                        </DropdownMenuItemWithIcon>
                                                    }
                                                    key={action.action}
                                                    action={action}
                                                />
                                            ))}
                                            {emergencyBarrierActions.map((action) => (
                                                <BarrierActionModalForm
                                                    triggerComponent={
                                                        <DropdownMenuItemWithIcon
                                                            onSelect={(e) => e.preventDefault()}
                                                            key={action.action}
                                                            variant="destructive"
                                                            icon={action.icon}
                                                        >
                                                            {formatMessage(action.title)}
                                                        </DropdownMenuItemWithIcon>
                                                    }
                                                    key={action.action}
                                                    action={action}
                                                    variant="destructiveLight"
                                                />
                                            ))}
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem asChild>
                                                <Link to="/actions">{formatMessage(actionMessages.allActions)}</Link>
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                )}
                            </>
                        )}

                        {userMemberships?.length > 1 && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button size={iconSize} variant="sm">
                                        <FontAwesomeIcon icon={faParkingCircle} className="text-base" />
                                        <span
                                            className={`hidden ${isParkingManagementScreen ? 'lg:block' : 'md:block'}`}
                                        >
                                            {parkingName}
                                        </span>
                                        <FontAwesomeIcon
                                            icon={fasChevronDown}
                                            className={`hidden text-xs ${isParkingManagementScreen ? 'lg:block' : 'md:block'}`}
                                        />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="z-40" align="end">
                                    <DropdownMenuLabel>
                                        {formatMessage(parkingManagementMessages.parkings)}
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <div className="max-h-60 overflow-y-auto">
                                        {userMemberships?.map(({ parking, role }) => (
                                            <DropdownMenuItem
                                                key={parking.id}
                                                onClick={() => {
                                                    handleParkingChange(parking, role);
                                                }}
                                                className="flex flex-col items-start"
                                            >
                                                <p>{parking.name}</p>
                                                <p className="text-xs text-gray-600">
                                                    {formatMessage(parkingManagementMessages.lastOpened)}
                                                    {': '}
                                                    {new Date(parking.updated_at).toLocaleString()}
                                                </p>
                                            </DropdownMenuItem>
                                        ))}
                                    </div>
                                    <DropdownMenuSeparator />
                                    {userMemberships && userMemberships.length > 0 && (
                                        <DropdownMenuItemWithIcon icon={faParkingCircle}>
                                            <Link to="/parking-management">
                                                {formatMessage(parkingManagementMessages.viewAllParkings)}
                                            </Link>
                                        </DropdownMenuItemWithIcon>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </div>
                )}
        </header>
    );
};

export default Header;
